import type { Role } from './auth';

export interface User {
  id: number;
  email: string;
  name: string;
  role: Role;
  slack_handle: string | null;
  is_active: boolean;
  created_at: string | null;
  last_login_at: string | null;
}

export interface UserCreate {
  email: string;
  name: string;
  role: Role;
  slack_handle?: string | null;
}

export type UserPatch = Partial<{
  role: Role;
  is_active: boolean;
  slack_handle: string | null;
  name: string;
}>;

export async function fetchUsers(includeDisabled = true): Promise<User[]> {
  const res = await fetch(`/api/v1/admin/users?include_disabled=${includeDisabled}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Users fetch failed: ${res.status}`);
  return res.json() as Promise<User[]>;
}

export async function createUser(body: UserCreate): Promise<User> {
  const res = await fetch('/api/v1/admin/users', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await extractError(res, 'Create failed'));
  }
  return res.json() as Promise<User>;
}

export async function patchUser(id: number, patch: UserPatch): Promise<void> {
  const res = await fetch(`/api/v1/admin/users/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    throw new Error(await extractError(res, 'Update failed'));
  }
}

/** Pull FastAPI's `{detail: "..."}` message out of an error response. */
async function extractError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === 'string') return body.detail;
    if (Array.isArray(body?.detail) && body.detail[0]?.msg) return body.detail[0].msg;
  } catch {
    /* not JSON */
  }
  return `${fallback}: ${res.status}`;
}
